/**
 * ReengagePanel — drafts ONE OR TWO variant follow-up emails via
 * GPT-5.2 for a went-cold template. The SE picks two angles for
 * A/B testing (or sticks with one for the legacy single-draft flow),
 * edits subject + body per variant, enters the prospect email, then
 * sends via Resend (with audit) — recording which variant won. Each
 * Send is independent so the SE can fire variant A now and B later.
 *
 * Stages:
 *   1. Input  — angle pickers + optional context note → "Generate"
 *   2. Preview — N variant cards (1 or 2), each independently
 *                editable + sendable; recipient input is shared
 *   3. Sent    — sticky summary of which variants were dispatched
 */
import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Loader2, RotateCw, ArrowLeft, Send, Mail, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import {
  useAnglePresets, useAngleRecommendation, AnglePicker,
  RecommendedPill, variantBadgeCls,
} from './_angles';

export const ReengagePanel = ({ templateId, templateName, totalVisits, industryPreset, onBack }) => {
  const angles = useAnglePresets();
  const rec = useAngleRecommendation(industryPreset);
  // 'input' | 'preview' | 'sent'
  const [stage, setStage] = useState('input');
  const [contextNote, setContextNote] = useState('');
  const [angleA, setAngleA] = useState('curiosity');
  const [angleB, setAngleB] = useState('roi');
  // Track whether the SE has manually changed an angle. Once true we
  // STOP auto-applying recommendation updates so a slow recommendation
  // payload landing after a deliberate override doesn't clobber it.
  const [angleATouched, setAngleATouched] = useState(false);
  const [angleBTouched, setAngleBTouched] = useState(false);
  const [abMode, setAbMode] = useState(true);
  const [draftLoading, setDraftLoading] = useState(false);
  const [variants, setVariants] = useState([]);
  const [recipientEmail, setRecipientEmail] = useState('');

  React.useEffect(() => {
    if (!rec?.available) return;
    if (!angleATouched && rec.recommended_a?.angle) setAngleA(rec.recommended_a.angle);
    if (!angleBTouched && rec.recommended_b?.angle) setAngleB(rec.recommended_b.angle);
  }, [rec, angleATouched, angleBTouched]);

  const onChangeA = (v) => { setAngleATouched(true); setAngleA(v); };
  const onChangeB = (v) => { setAngleBTouched(true); setAngleB(v); };

  const generateDraft = async () => {
    if (abMode && angleA === angleB) {
      toast.error('Pick two different angles to A/B test.');
      return;
    }
    setDraftLoading(true);
    try {
      const payload = {
        context_note: contextNote,
        angles: abMode ? [angleA, angleB] : [angleA],
      };
      const res = await api.post(
        `/admin/demo-director/templates/${templateId}/reengage-draft`,
        payload,
      );
      let next;
      if (res.data?.variants) {
        next = res.data.variants.map((v) => ({ ...v, status: 'idle' }));
      } else {
        next = [{
          variant: 'A',
          angle: res.data.angle || angleA,
          label: angles.find((a) => a.key === (res.data.angle || angleA))?.label || 'Variant A',
          subject: res.data.subject,
          body_text: res.data.body_text,
          body_html: res.data.body_html,
          draft_hash: res.data.draft_hash,
          status: 'idle',
        }];
      }
      setVariants(next);
      setStage('preview');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Draft generation failed');
    } finally {
      setDraftLoading(false);
    }
  };

  const updateVariant = (i, patch) => {
    setVariants((prev) => prev.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  };

  const sendVariant = async (i) => {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      toast.error('Enter the prospect email address first');
      return;
    }
    const v = variants[i];
    updateVariant(i, { status: 'sending' });
    try {
      const res = await api.post(
        `/admin/demo-director/templates/${templateId}/reengage-send`,
        {
          recipient_email: recipientEmail.trim(),
          subject: v.subject,
          body_html: v.body_html,
          body_text: v.body_text,
          draft_hash: v.draft_hash,
          variant: v.variant,
          variant_angle: v.angle,
        },
      );
      updateVariant(i, {
        status: res.data.sent ? 'sent' : 'send_failed',
        sent_to: res.data.recipient_email,
      });
      if (res.data.sent) {
        toast.success(`Variant ${v.variant} sent to ${res.data.recipient_email}`);
      } else {
        toast.error('Send attempt logged but Resend reported failure — check RESEND_API_KEY config');
      }
      const remaining = variants.filter((x, idx) => idx !== i && x.status !== 'sent' && x.status !== 'send_failed');
      if (remaining.length === 0) setStage('sent');
    } catch (err) {
      updateVariant(i, { status: 'send_failed' });
      toast.error(err?.response?.data?.detail || 'Send failed');
    }
  };

  const openInMailClient = (i) => {
    const v = variants[i];
    if (!v) return;
    const to = recipientEmail.trim();
    const subj = encodeURIComponent(v.subject);
    const body = encodeURIComponent(v.body_text);
    window.location.href = `mailto:${to}?subject=${subj}&body=${body}`;
  };

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" data-testid="dd-reengage-panel">
      <div className="flex items-center justify-between gap-2">
        <Button
          size="sm" variant="ghost" onClick={onBack}
          className="h-7 text-[11px] text-slate-600 hover:text-rose-600"
          data-testid="dd-reengage-back-btn"
        >
          <ArrowLeft className="h-3 w-3 mr-1" /> Back to timeline
        </Button>
        <span className="text-[11px] text-slate-500">
          Re-engaging <strong className="text-slate-800">{templateName}</strong>
          <span className="ml-1 text-slate-400">· {totalVisits} historical visits</span>
        </span>
      </div>

      {stage === 'input' && (
        <div className="space-y-3" data-testid="dd-reengage-input-stage">
          <div className="rounded-lg border border-rose-100 bg-rose-50/40 p-3 text-[12px] text-rose-800">
            <p className="font-semibold mb-1">🪄 A/B subject-line tester</p>
            <p className="text-rose-700/80">
              Pick two angles and GPT-5.2 will draft both in parallel. Each variant lands in its
              own card so you can edit independently and pick a winner per recipient — or switch
              off A/B mode for a single nudge.
            </p>
          </div>

          <div className="rounded-md border border-slate-200 bg-white p-2 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 cursor-pointer">
              <input
                type="checkbox" checked={abMode}
                onChange={(e) => setAbMode(e.target.checked)}
                className="accent-rose-600"
                data-testid="dd-reengage-ab-mode-toggle"
              />
              A/B test two angles
            </label>
            <div className="flex items-center gap-1">
              <AnglePicker
                value={angleA} onChange={onChangeA} angles={angles}
                label={abMode ? 'Variant A' : 'Angle'}
                testId="dd-reengage-angle-a-select"
              />
              {!angleATouched && rec?.available && rec.recommended_a?.angle === angleA && (
                <RecommendedPill rec={rec.recommended_a} scope={rec.scope} testId="dd-reengage-angle-a-rec-pill" />
              )}
            </div>
            {abMode && (
              <div className="flex items-center gap-1">
                <AnglePicker
                  value={angleB} onChange={onChangeB} angles={angles}
                  label="Variant B"
                  testId="dd-reengage-angle-b-select"
                />
                {!angleBTouched && rec?.available && rec.recommended_b?.angle === angleB && (
                  <RecommendedPill rec={rec.recommended_b} scope={rec.scope} testId="dd-reengage-angle-b-rec-pill" />
                )}
              </div>
            )}
            {rec?.available && (
              <span className="text-[10px] text-slate-500 ml-auto" data-testid="dd-reengage-rec-scope">
                ✨ Auto-recommended from {rec.scope === 'industry' ? `${industryPreset || 'industry'} data` : 'global data'}
              </span>
            )}
          </div>

          <div>
            <label className="text-[11px] font-semibold text-slate-700 block mb-1" htmlFor="dd-reengage-context-note">
              Optional context about the prospect
            </label>
            <Textarea
              id="dd-reengage-context-note"
              value={contextNote}
              onChange={(e) => setContextNote(e.target.value)}
              placeholder="e.g. CHRO at a 5K-employee manufacturer who lost 30% bench depth last year. Wants to discuss before Q3 board prep."
              rows={3}
              className="text-[12px]"
              data-testid="dd-reengage-context-note"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Same context flows into both variants, anchored on each angle's framing.
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm" onClick={generateDraft} disabled={draftLoading}
              className="h-8 gap-1.5 bg-rose-600 text-white hover:bg-rose-700"
              data-testid="dd-reengage-generate-btn"
            >
              {draftLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {abMode ? 'Generate variants' : 'Generate draft'}
            </Button>
          </div>
        </div>
      )}

      {stage === 'preview' && variants.length > 0 && (
        <div className="space-y-3" data-testid="dd-reengage-preview-stage">
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-2 text-[11px] text-emerald-800 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> {variants.length === 2 ? 'Two variants' : 'Draft'} generated. Edit, then pick which to send.
          </div>

          <div>
            <label className="text-[11px] font-semibold text-slate-700 block mb-1" htmlFor="dd-reengage-recipient">
              Prospect email <span className="text-slate-400 font-normal">(shared across variants)</span>
            </label>
            <Input
              id="dd-reengage-recipient" type="email"
              placeholder="firstname@company.com"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              className="text-[12px]"
              data-testid="dd-reengage-recipient-input"
            />
          </div>

          <div className={`grid gap-3 ${variants.length === 2 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
            {variants.map((v, i) => (
              <div
                key={`${v.variant}-${i}`}
                className={`rounded-lg border p-3 space-y-2 ${
                  v.status === 'sent' ? 'border-emerald-200 bg-emerald-50/40'
                  : v.status === 'send_failed' ? 'border-rose-200 bg-rose-50/40'
                  : 'border-slate-200 bg-white'
                }`}
                data-testid={`dd-reengage-variant-${v.variant}`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className={`inline-flex items-center font-semibold rounded-full ring-1 text-[10px] py-0.5 px-1.5 gap-0.5 ${variantBadgeCls(v.variant)}`}>
                    Variant {v.variant} · {v.label}
                  </span>
                  <span className="text-[10px]">
                    {v.status === 'sent' && (
                      <span className="text-emerald-700 font-semibold" data-testid={`dd-reengage-variant-${v.variant}-sent-marker`}>✓ Sent to {v.sent_to}</span>
                    )}
                    {v.status === 'send_failed' && (
                      <span className="text-rose-700 font-semibold">✗ Send failed (audit logged)</span>
                    )}
                  </span>
                </div>
                <Input
                  value={v.subject}
                  onChange={(e) => updateVariant(i, { subject: e.target.value })}
                  className="text-[12px] font-medium"
                  placeholder="Subject"
                  disabled={v.status === 'sent'}
                  data-testid={`dd-reengage-variant-${v.variant}-subject-input`}
                />
                <Textarea
                  value={v.body_text}
                  onChange={(e) => {
                    const newText = e.target.value;
                    const html = newText.split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');
                    updateVariant(i, { body_text: newText, body_html: html });
                  }}
                  rows={7}
                  className="text-[11px] font-mono"
                  disabled={v.status === 'sent'}
                  data-testid={`dd-reengage-variant-${v.variant}-body-input`}
                />
                <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => openInMailClient(i)}
                    className="h-7 text-[11px] gap-1 border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid={`dd-reengage-variant-${v.variant}-mailto-btn`}
                  >
                    <Mail className="h-3 w-3" /> Mail client
                  </Button>
                  <Button
                    size="sm" onClick={() => sendVariant(i)}
                    disabled={v.status === 'sending' || v.status === 'sent' || !recipientEmail.includes('@')}
                    className="h-7 text-[11px] gap-1.5 bg-rose-600 text-white hover:bg-rose-700"
                    data-testid={`dd-reengage-variant-${v.variant}-send-btn`}
                  >
                    {v.status === 'sending' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                    Send {v.variant}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-1">
            <Button
              size="sm" variant="outline"
              onClick={() => { setStage('input'); setVariants([]); }}
              className="h-7 text-[11px] gap-1"
              data-testid="dd-reengage-regenerate-btn"
            >
              <RotateCw className="h-3 w-3" /> Regenerate
            </Button>
          </div>
        </div>
      )}

      {stage === 'sent' && variants.length > 0 && (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4 text-[12px] text-emerald-900 space-y-3"
          data-testid="dd-reengage-sent-stage"
        >
          <p className="font-semibold flex items-center gap-1">
            <Send className="h-3 w-3" /> Done — {variants.filter((v) => v.status === 'sent').length}/{variants.length} variant(s) dispatched
          </p>
          <ul className="space-y-1">
            {variants.map((v) => (
              <li key={v.variant} className="text-[11px] flex items-baseline gap-2">
                <span className={`inline-flex items-center rounded-full ring-1 text-[9px] py-px px-1.5 font-semibold ${variantBadgeCls(v.variant)}`}>
                  {v.variant} · {v.label}
                </span>
                <span className="text-emerald-800">{v.subject}</span>
                <span className="ml-auto text-[10px] text-emerald-700/80">
                  {v.status === 'sent' ? `→ ${v.sent_to}` : v.status === 'send_failed' ? 'send failed' : 'not sent'}
                </span>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 pt-1">
            <Button
              size="sm" variant="outline"
              onClick={() => { setStage('input'); setVariants([]); setRecipientEmail(''); }}
              className="h-7 text-[11px] gap-1"
              data-testid="dd-reengage-restart-btn"
            >
              <Sparkles className="h-3 w-3" /> Draft another
            </Button>
            <Button
              size="sm" variant="outline" onClick={onBack}
              className="h-7 text-[11px] gap-1"
              data-testid="dd-reengage-done-btn"
            >
              <ArrowLeft className="h-3 w-3" /> Back to timeline
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
